import { useCallback, useEffect, useMemo, useRef } from 'react'

import { F0Select, F0SelectItemProps } from '@factorialco/f0-react'
import {
  AtsVacancySelectorDataDocument,
  AtsVacancySelectorItemFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  jobPostingId: string
  value?: string
  onChange: (vacancyId: string) => void
  applicationId?: string
}

export const VacancySelector = ({ jobPostingId, value, onChange, applicationId }: Props) => {
  const { employee: sessionEmployee } = useCurrentSession()
  const hasAutoSelected = useRef(false)
  const prevApplicationId = useRef(applicationId)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const stableOnChange = useCallback((vacancyId: string) => {
    onChangeRef.current(vacancyId)
  }, [])

  const { loading, data } = useQuery(AtsVacancySelectorDataDocument, {
    variables: {
      bestMatchForAtsApplicationId: applicationId,
    },
  })

  const vacancies = useMemo(() => data?.ats.vacanciesConnection.nodes ?? [], [data])

  const bestMatchId = useMemo(() => {
    if (!applicationId) return undefined
    const bestMatch = vacancies.find((v) => v.isBestMatch)
    return bestMatch ? String(bestMatch.id) : undefined
  }, [vacancies, applicationId])

  useEffect(() => {
    if (prevApplicationId.current !== applicationId) {
      prevApplicationId.current = applicationId
      hasAutoSelected.current = false
    }
  }, [applicationId])

  useEffect(() => {
    if (bestMatchId && !value && !hasAutoSelected.current) {
      hasAutoSelected.current = true
      stableOnChange(bestMatchId)
    }
  }, [bestMatchId, value, stableOnChange])

  const options: F0SelectItemProps<string>[] = useMemo(() => {
    const toOption = (vacancy: AtsVacancySelectorItemFragment): F0SelectItemProps<string> => {
      const description =
        [
          vacancy.location?.name,
          [vacancy.jobCatalogTreeRole?.name, vacancy.jobCatalogTreeLevel?.name]
            .filter(Boolean)
            .join(' ') || undefined,
          vacancy.team?.name,
        ]
          .filter(Boolean)
          .join(' / ') || undefined

      return {
        value: String(vacancy.id),
        label: vacancy.name,
        description,
        tag: vacancy.lineManager?.fullName,
      }
    }

    const sortByLinkedFirst = (
      a: AtsVacancySelectorItemFragment,
      b: AtsVacancySelectorItemFragment
    ) => {
      const aLinked = a.jobPosting?.id === jobPostingId ? 0 : 1
      const bLinked = b.jobPosting?.id === jobPostingId ? 0 : 1
      return aLinked - bLinked
    }

    const bestMatch = applicationId ? vacancies.find((v) => v.isBestMatch) : undefined
    const rest = applicationId ? vacancies.filter((v) => !v.isBestMatch) : vacancies

    const assignedToMe: AtsVacancySelectorItemFragment[] = []
    const allVacancies: AtsVacancySelectorItemFragment[] = []

    for (const vacancy of rest) {
      if (vacancy.atsVacancyHiringManagers.some((hm) => hm.id === sessionEmployee.id)) {
        assignedToMe.push(vacancy)
      } else {
        allVacancies.push(vacancy)
      }
    }

    assignedToMe.sort(sortByLinkedFirst)
    allVacancies.sort(sortByLinkedFirst)

    const result: F0SelectItemProps<string>[] = []

    if (bestMatch) {
      result.push({
        value: '__best_match_header__',
        label: i18n.t('ats.vacancy_selector.best_match'),
        disabled: true,
      })
      result.push(toOption(bestMatch))
    }

    if (assignedToMe.length > 0) {
      if (result.length > 0) {
        result.push({ type: 'separator' })
      }
      result.push({
        value: '__assigned_to_me_header__',
        label: i18n.t('ats.vacancy_selector.assigned_to_me'),
        disabled: true,
      })
      result.push(...assignedToMe.map(toOption))
    }

    if (allVacancies.length > 0) {
      if (result.length > 0) {
        result.push({ type: 'separator' })
      }
      result.push({
        value: '__all_vacancies_header__',
        label: i18n.t('ats.vacancy_selector.all_vacancies'),
        disabled: true,
      })
      result.push(...allVacancies.map(toOption))
    }

    return result
  }, [vacancies, sessionEmployee.id, jobPostingId, applicationId])

  return (
    <>
      <F0Select
        label={i18n.t('ats.vacancy_selector.select_label')}
        name="vacancy"
        placeholder={i18n.t('ats.vacancy_selector.select_placeholder')}
        size="md"
        value={value}
        onChange={stableOnChange}
        options={options}
        showSearchBox
        searchBoxPlaceholder={i18n.t('ats.vacancy_selector.search_placeholder')}
        searchEmptyMessage={i18n.t('ats.vacancy_selector.search_no_results')}
        readonly={loading}
      />
      {bestMatchId && (
        <div
          data-testid="auto-suggest-banner"
          style={{
            background:
              'linear-gradient(to left, rgba(161, 173, 229, 0.12), rgba(229, 25, 67, 0.12), rgba(229, 86, 25, 0.12))',
            borderRadius: '12px',
            padding: '8px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginTop: '8px',
          }}
        >
          <strong style={{ fontSize: '14px' }}>
            {i18n.t('ats.vacancy_selector.auto_suggest_title')}
          </strong>
          <span style={{ fontSize: '13px', color: 'hsl(var(--neutral-500, 220 9% 46%))' }}>
            {i18n.t('ats.vacancy_selector.auto_suggest_description')}
          </span>
        </div>
      )}
    </>
  )
}
